import React, { useState, useEffect } from 'react'
import "./homepage.modern.css"
import { useNavigate } from "react-router-dom"
import HeroSlider from './HeroSlider'
import FeaturedVideoSection from './FeaturedVideoSection' 

export default function ModernHomepage() {
    const navigate = useNavigate()
    const [brandImages, setBrandImages] = useState({})
    const [collections, setCollections] = useState([])
    const [siteSettings, setSiteSettings] = useState({})
    const [brands, setBrands] = useState([])
    const [categories, setCategories] = useState([])

    // Brand logo mapping for placeholders
    const brandLogos = {
        'opi': '/images/OPI.png',
        'pla': '/images/PLAcolor.jpg',
        'niko': '/images/NIKO.png',
    };

    useEffect(() => {
        const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        fetch(`${API}/brands`)
            .then(res => res.json())
            .then(data => {
                if (data.success && data.data) {
                    const active = data.data.filter(b => b.product_count > 0);
                    setBrands(active.map(b => ({ name: b.name, slug: b.slug, image_url: b.image_url })));
                    const images = {};
                    active.forEach(b => {
                        if (b.image_url) images[b.slug] = b.image_url;
                        else if (b.sample_image) images[b.slug] = b.sample_image;
                    });
                    console.log(images);
                    setBrandImages(images);
                }
            })
            .catch(err => console.error('Failed to fetch brands:', err));
    }, [])

    useEffect(() => {
        const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        fetch(`${API}/collections/active`)
            .then(res => res.json())
            .then(data => setCollections(data))
            .catch(err => console.error('Failed to fetch collections:', err));
        fetch(`${API}/site-settings`)
            .then(res => res.json())
            .then(data => setSiteSettings(data))
            .catch(err => console.error('Failed to fetch site settings:', err));
        fetch(`${API}/categories/homepage`)
            .then(res => res.json())
            .then(data => {
                if (data.success && data.data && data.data.length > 0) {
                    setCategories(data.data.map(c => ({
                        name: c.name,
                        slug: c.slug,
                        image: c.image_url || defaultCategoryImages[c.slug] || null
                    })));
                } else {
                    setCategories(fallbackCategories);
                }
            })
            .catch(() => setCategories(fallbackCategories));
    }, []);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

    const defaultCategoryImages = {
        'gel-polish': '/images/products/nails/PLA.webp',
        'lacquer-polish': '/images/products/nails/nail_lacquer.webp',
        'gel-lacquer-combo': '/images/products/nails/PLA.webp',
        'dip-acrylic-powder': '/images/products/nails/dipping_powder.webp',
        'nail-art': '/images/products/nails/shop_nail_arts.webp',
        'led-uv-lamps': '/images/products/nails/gel_lamps.webp',
        'pedicure-spa': '/images/products/nails/shop_pedicure_chairs.webp',
        'tools-equipment': '/images/products/nails/nail_tools.webp',
    };

    const fallbackCategories = [
        { name: 'Gel Polish', slug: 'gel-polish', image: '/images/products/nails/PLA.webp' },
        { name: 'Lacquer Polish', slug: 'lacquer-polish', image: '/images/products/nails/nail_lacquer.webp' },
        { name: 'Gel & Lacquer', slug: 'gel-lacquer-combo', image: '/images/products/nails/PLA.webp' },
        { name: 'Dip & Acrylic', slug: 'dip-acrylic-powder', image: '/images/products/nails/dipping_powder.webp' },
        { name: 'Nail Art', slug: 'nail-art', image: '/images/products/nails/shop_nail_arts.webp' },
        { name: 'LED & UV Lamps', slug: 'led-uv-lamps', image: '/images/products/nails/gel_lamps.webp' },
        { name: 'Pedicure & Spa', slug: 'pedicure-spa', image: '/images/products/nails/shop_pedicure_chairs.webp' },
        { name: 'Tools & Equipment', slug: 'tools-equipment', image: '/images/products/nails/nail_tools.webp' }
    ];

    const getImageUrl = (localPath) => {
        if (!localPath) return null;
        if (localPath.startsWith('http')) return localPath;
        const baseUrl = API_URL.replace('/api', '');
        if (localPath.startsWith('/uploads/')) return `${baseUrl}${localPath}`;
        return `${baseUrl}/api/images/${localPath}`;
    }

    return (
        <div className="modern-homepage">
            {/* Hero Slider */}
            {siteSettings.show_hero_slider !== 'false' && <HeroSlider />}

            <FeaturedVideoSection />

            {/* Trust Indicators Below Hero */}
            <section className="trust-section">
                <div className="container-modern">
                    <div className="trust-indicators-row">
                        <div className="trust-item-modern">
                            <span>Shop 24/7 Online</span>
                        </div>
                        <div className="trust-item-modern">
                            <span>100% Authentic Brands</span>
                        </div>
                        <div className="trust-item-modern">
                            <span>Same-Day Shipping</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Shop by Category */}
            {siteSettings.show_categories !== 'false' && (
            <section className="section-modern section-category">
                <div className="container-modern">
                    <div className="section-header-modern">
                        <h2 className="section-title-modern">Shop by Category</h2>
                        <p className="section-subtitle-modern">Find exactly what you need</p>
                    </div>

                    <div className="category-grid-modern">
                        {categories.map((category, index) => (
                            <div 
                                key={index}
                                className="category-card-modern"
                                onClick={() => navigate(`/products?category=${category.slug}`)}
                            >
                                <div className="category-image-wrapper">
                                    <img src={category.image?.startsWith('/uploads/') ? getImageUrl(category.image) : category.image} alt={category.name} />
                                </div>
                                <h3 className="category-name-modern">{category.name}</h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* Shop by Brand */}
            {siteSettings.show_brands !== 'false' && (
            <section className="section-modern section-grey">
                <div className="container-modern">
                    <div className="section-header-modern">
                        <h2 className="section-title-modern">Shop by Brand</h2>
                        <p className="section-subtitle-modern">Explore our premium brands</p>
                    </div>

                    <div className="brands-grid-modern">
                        {brands.map((brand, index) => (
                            <div 
                                key={index}
                                className="brand-card-modern"
                                onClick={() => navigate(`/brands/${brand.slug.replace(/-/g, '+')}`)}
                            >
                                <div className="brand-logo-wrapper">
                                    {brandImages[brand.slug] || brandLogos[brand.slug] ? (
                                        <img 
                                            src={brandImages[brand.slug] ? getImageUrl(brandImages[brand.slug]) : brandLogos[brand.slug]} 
                                            alt={brand.name}
                                            onError={(e) => {
                                                // Fallback to placeholder if image fails to load
                                                if (brandLogos[brand.slug]) {
                                                    e.target.src = brandLogos[brand.slug];
                                                } else {
                                                    e.target.style.display = 'none';
                                                }
                                            }}
                                        />
                                    ) : (
                                        <div className="brand-loading">No Image</div>
                                    )}
                                </div>
                                <h3 className="brand-name-modern">{brand.name}</h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* Featured Collections */}
            {siteSettings.show_collections !== 'false' && (
            <section className="section-modern" style={{ background: 'linear-gradient(135deg, #1a0a1a 0%, #2d1b2d 50%, #1a0a1a 100%)' }}>
                <div className="container-modern">
                    <div className="section-header-modern">
                        <h2 className="section-title-modern" style={{color: 'white'}}>Featured Collections</h2>
                        <p className="section-subtitle-modern" style={{color: 'white'}}>Explore our premium brands</p>
                    </div>
                    <div className="featured-collections">
                        {collections.map(col => (
                            <div key={col.id} className="collection-large" onClick={() => navigate(col.link_url)}>
                                <img src={col.image_url} alt={col.title} />
                                <div className="collection-overlay">
                                    <h3>{col.title}</h3>
                                    <p>{col.subtitle}</p>
                                    <button className="btn-white-modern">{col.button_text}</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            )}



            {/* Social Proof */}
            {siteSettings.show_testimonials !== 'false' && (
            <section className="section-modern section-grey">
                <div className="container-modern">
                    <div className="section-header-modern">
                        <h2 className="section-title-modern">Trusted by Professionals</h2>
                        <p className="section-subtitle-modern">See what our customers are saying</p>
                    </div>

                    <div className="testimonials-grid">
                        <div className="testimonial-card">
                            <div className="testimonial-rating">5.0</div>
                            <p className="testimonial-text">"Best supplier for my salon. Fast shipping and authentic products every time."</p>
                            <p className="testimonial-author">— Sarah M., Salon Owner</p>
                        </div>
                        <div className="testimonial-card">
                            <div className="testimonial-rating">5.0</div>
                            <p className="testimonial-text">"Amazing selection and prices. TC Nails Depot is my go-to for all supplies."</p>
                            <p className="testimonial-author">— Jennifer L., Nail Tech</p>
                        </div>
                        <div className="testimonial-card">
                            <div className="testimonial-rating">5.0</div>
                            <p className="testimonial-text">"Professional quality products at wholesale prices. Highly recommend!"</p>
                            <p className="testimonial-author">— Maria R., Beauty Professional</p>
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* Newsletter */}
            {siteSettings.show_newsletter !== 'false' && (
            <section className="newsletter-modern">
                <div className="container-modern">
                    <div className="newsletter-content">
                        <h2 className="newsletter-title">{siteSettings.newsletter_title || 'Get 10% Off Your First Order'}</h2>
                        <p className="newsletter-subtitle">{siteSettings.newsletter_subtitle || 'Join our community and stay updated on new arrivals and exclusive deals'}</p>
                        <form className="newsletter-form">
                            <input 
                                type="email" 
                                placeholder="Enter your email address" 
                                className="newsletter-input"
                            />
                            <button type="submit" className="newsletter-btn">Subscribe</button>
                        </form>
                        <p className="newsletter-privacy">We respect your privacy. Unsubscribe anytime.</p>
                    </div>
                </div>
            </section>
            )}
        </div>
    )
}
